import React, { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../utils/supabase';

const AuthContext = createContext({});

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [profileResolvedFor, setProfileResolvedFor] = useState(null);
  const [authResolving, setAuthResolving] = useState(true);
  const [authError, setAuthError] = useState(null);

  const userRef = useRef(null);
  const requestIdRef = useRef(0);

  const runProfileFetch = useCallback(async (userId, requestId) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      if (error) throw error;
      if (requestIdRef.current !== requestId) return;
      setProfileData(data || null);
      setAuthError(null);
    } catch (err) {
      if (requestIdRef.current !== requestId) return;
      setProfileData(null);
      setAuthError(err.message || 'Failed to load profile.');
    }
  }, []);

  const fetchProfile = useCallback((userId) => {
    const requestId = ++requestIdRef.current;
    return runProfileFetch(userId, requestId).finally(() => {
      if (requestIdRef.current === requestId) {
        setProfileResolvedFor(userId);
      }
    });
  }, [runProfileFetch]);

  useEffect(() => {
    const getInitialSession = async () => {
      const safetyTimeout = setTimeout(() => {
        setAuthResolving(false);
      }, 8000);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          userRef.current = session.user;
          setUser(session.user);
        } else {
          userRef.current = null;
          setUser(null);
        }
      } catch (err) {
        userRef.current = null;
        setUser(null);
      } finally {
        clearTimeout(safetyTimeout);
        setAuthResolving(false);
      }
    };

    getInitialSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'TOKEN_REFRESHED') {
          // Token silently refreshed — do nothing, user and profile unchanged
          return;
        }
        if (event === 'SIGNED_IN' && session?.user) {
          userRef.current = session.user;
          setUser(session.user);
        } else if (event === 'SIGNED_OUT') {
          userRef.current = null;
          setUser(null);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const userId = user?.id ?? null;

  useEffect(() => {
    if (!userId) {
      requestIdRef.current += 1;
      setProfileData(null);
      setProfileResolvedFor(null);
      return;
    }

    const requestId = ++requestIdRef.current;

    runProfileFetch(userId, requestId).finally(() => {
      if (requestIdRef.current === requestId) {
        setProfileResolvedFor(userId);
      }
    });
  }, [userId, runProfileFetch]);

  const profile = useMemo(() => (
    user && profileResolvedFor === user.id ? profileData : null
  ), [user, profileResolvedFor, profileData]);

  const loading = authResolving || (!!user && profileResolvedFor !== user.id);

  const signUp = async ({ email, password, fullName, role, sport, secondarySport, city, state, bio, hourlyRate, age, focusAreas, sportsInterest, focusAreasInterest, gradYear }) => {
    const nameParts = fullName.trim().split(' ');
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(' ') || 'N/A';

    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;

    const newUser = data.user;
    if (!newUser) throw new Error('Signup succeeded but no user returned.');

    await new Promise(resolve => setTimeout(resolve, 400));

    const { error: profileError } = await supabase
      .from('profiles')
      .update({
        first_name: firstName,
        last_name: lastName,
        role,
        sport: sport || null,
        secondary_sport: secondarySport || null,
        bio: bio || null,
        city: city || null,
        state: state || null,
        age: age ? parseInt(age) : null,
        hourly_rate: hourlyRate ? parseFloat(hourlyRate) : null,
        tags: focusAreas && focusAreas.length > 0 ? focusAreas : null,
        focus_areas: focusAreas?.length ? focusAreas : null,
        sports_interest: sportsInterest && sportsInterest.length > 0 ? sportsInterest : null,
        focus_areas_interest: focusAreasInterest && focusAreasInterest.length > 0 ? focusAreasInterest : null,
        grad_year: role === 'mentor' ? (gradYear ? parseInt(gradYear) : null) : null,
      })
      .eq('id', newUser.id);

    if (profileError) throw profileError;

    userRef.current = newUser;
    setUser(newUser);
    return data;
  };

  const signIn = async ({ email, password }) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    requestIdRef.current += 1;
    userRef.current = null;
    setUser(null);
    setProfileData(null);
    setProfileResolvedFor(null);
    await supabase.auth.signOut();
  };

  const updateProfile = async (updates) => {
    if (!user) throw new Error('Not authenticated.');
    const { data: updated, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id)
      .select()
      .maybeSingle();
    if (error) throw error;
    requestIdRef.current += 1;
    setProfileData(updated);
    setProfileResolvedFor(user.id);
    return updated;
  };

  const clearAuthError = () => setAuthError(null);

  return (
    <AuthContext.Provider value={{ user, profile, loading, authError, clearAuthError, signUp, signIn, signOut, updateProfile, fetchProfile }}>
      {children}
    </AuthContext.Provider>
  );
}
